import React, { useEffect, useState } from "react";
import { Search, Eye, Printer, Download } from "lucide-react";
import { toast } from "sonner";
import api, { taka } from "@/lib/api";
import { downloadOrderInvoice } from "@/lib/invoice";
import { Modal, Th, Td, Empty, inputCls, fmtDate } from "@/pages/admin/AdminUI";
import { STATUS_LABEL, STATUS_COLOR, ORDER_STATUSES } from "@/lib/bd";

const FLOW = [...ORDER_STATUSES.map((s) => s.key), "cancelled"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [view, setView] = useState(null);

  const load = () => api.get("/admin/orders", { params: { status: status || undefined, search: q || undefined } }).then((r) => setOrders(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const changeStatus = async (id, s) => {
    try { await api.put(`/admin/orders/${id}/status`, { status: s }); toast.success("স্ট্যাটাস আপডেট হয়েছে"); load(); if (view) setView({ ...view, status: s }); }
    catch (e) { toast.error(e.response?.data?.detail || "ত্রুটি"); }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-[#1E3A2B]">অনলাইন অর্ডার</h1>
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="অর্ডার নং / নাম / মোবাইল" className={`${inputCls} pl-9`} data-testid="order-search" />
          <Search size={16} className="absolute left-3 top-3.5 text-stone-400" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} max-w-[180px]`} data-testid="order-status-filter">
          <option value="">সব স্ট্যাটাস</option>
          {FLOW.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead className="bg-stone-50 border-b border-stone-200"><tr><Th>অর্ডার নং</Th><Th>কাস্টমার</Th><Th>তারিখ</Th><Th>মোট</Th><Th>পেমেন্ট</Th><Th>স্ট্যাটাস</Th><Th></Th></tr></thead>
          <tbody className="divide-y divide-stone-100">
            {orders.map((o) => (
              <tr key={o.id} data-testid={`order-row-${o.order_no}`}>
                <Td className="font-semibold text-[#1E3A2B]">{o.order_no}</Td>
                <Td><div>{o.customer_name}</div><div className="text-xs text-stone-400">{o.mobile}</div></Td>
                <Td>{fmtDate(o.created_at)}</Td>
                <Td className="font-semibold">{taka(o.total)}</Td>
                <Td className="uppercase text-xs">{o.payment_method}</Td>
                <Td>
                  <select value={o.status} onChange={(e) => changeStatus(o.id, e.target.value)} className={`text-xs font-semibold px-2 py-1 rounded-full border-0 ${STATUS_COLOR[o.status]}`} data-testid={`order-status-${o.order_no}`}>
                    {FLOW.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                </Td>
                <Td><button onClick={() => setView(o)} className="p-2 text-blue-600 hover:bg-stone-100 rounded-lg" data-testid={`view-order-${o.order_no}`}><Eye size={16} /></button></Td>
              </tr>
            ))}
          </tbody>
        </table>
        {orders.length === 0 && <Empty text="কোনো অর্ডার নেই" />}
      </div>

      <Modal open={!!view} onClose={() => setView(null)} title={view?.order_no} wide>
        {view && (
          <div className="space-y-4" id="invoice-print">
            <div className="flex justify-between text-sm">
              <div><div className="font-semibold text-[#1E3A2B]">{view.customer_name}</div><div className="text-stone-500">{view.mobile}</div><div className="text-stone-500">{view.address}, {view.upazila}, {view.district}</div></div>
              <div className="text-right"><div className="text-stone-500">{fmtDate(view.created_at)}</div><span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_COLOR[view.status]}`}>{STATUS_LABEL[view.status]}</span></div>
            </div>
            <table className="w-full text-sm border-t border-stone-200">
              <thead><tr className="text-left text-stone-400 text-xs"><th className="py-2">পণ্য</th><th>দাম</th><th>পরিমাণ</th><th className="text-right">মোট</th></tr></thead>
              <tbody>
                {view.items.map((it, i) => (
                  <tr key={i} className="border-t border-stone-100"><td className="py-2">{it.name_bn}</td><td>{taka(it.price)}</td><td>{it.quantity} {it.unit}</td><td className="text-right font-medium">{taka(it.line_total)}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="text-sm space-y-1 border-t border-stone-200 pt-2">
              <div className="flex justify-between"><span className="text-stone-500">সাবটোটাল</span><span>{taka(view.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">ডেলিভারি</span><span>{taka(view.delivery_charge)}</span></div>
              {view.discount > 0 && <div className="flex justify-between text-emerald-700"><span>ছাড়</span><span>-{taka(view.discount)}</span></div>}
              <div className="flex justify-between font-bold text-[#1E3A2B] text-base"><span>সর্বমোট</span><span>{taka(view.total)}</span></div>
            </div>
            <button onClick={() => window.print()} className="w-full bg-stone-800 text-white h-11 rounded-xl flex items-center justify-center gap-2 font-medium" data-testid="print-invoice"><Printer size={16} /> ইনভয়েস প্রিন্ট</button>
            <button onClick={() => downloadOrderInvoice(view)} className="w-full bg-[#CA8A04] text-white h-11 rounded-xl flex items-center justify-center gap-2 font-medium" data-testid="download-order-pdf"><Download size={16} /> PDF ডাউনলোড</button>
          </div>
        )}
      </Modal>
    </div>
  );
}
